import React, { useEffect, useState, useRef } from 'react';
import { Users, Plus, Pencil, Archive, ArchiveRestore, Search, UserCheck, ArrowLeft, ChevronLeft, ChevronRight, Save, Trash2 } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const rowRefs = useRef({});
  const { apiFetch } = useApi();

  useEffect(() => { load(); }, [showArchived]);
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(users.filter(u =>
      (u.verein || '').toLowerCase().includes(q) ||
      (u.club_id || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    ));
    setSelectedIdx(null);
    setIsNew(false);
  }, [search, users]);

  const load = async () => {
    setLoading(true);
    const url = showArchived ? '/api/providers?include_archived=true' : '/api/providers';
    const r = await apiFetch(url);
    const d = await r?.json();
    if (d) setUsers(showArchived ? d.filter(u => u.archived) : d);
    setLoading(false);
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 4000); };

  const selectUser = (idx) => {
    const u = filtered[idx];
    setSelectedIdx(idx);
    setIsNew(false);
    setForm({
      verein: u.verein || '', club_id: u.club_id || '', email: u.email || '', password: '',
      ik_number: u.ik_number || '', street: u.street || '', postal_code: u.postal_code || '',
      city_location: u.city_location || '', phone: u.phone || '', contact_person: u.contact_person || '',
      website: u.website || '',
    });
  };

  const openNew = () => {
    setSelectedIdx(null);
    setIsNew(true);
    setForm({ verein: '', club_id: '', email: '', password: '', ik_number: '', street: '', postal_code: '', city_location: '', phone: '', contact_person: '', website: '' });
  };

  const goBack = () => {
    const lastIdx = selectedIdx;
    setSelectedIdx(null);
    setIsNew(false);
    if (lastIdx !== null) {
      setTimeout(() => {
        const id = filtered[lastIdx]?.id;
        if (id && rowRefs.current[id]) rowRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  };

  const navigate = (dir) => {
    const newIdx = selectedIdx + dir;
    if (newIdx >= 0 && newIdx < filtered.length) selectUser(newIdx);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (isNew) {
      if (!form.password || form.password.length < 8) { showFb('error', 'Passwort mind. 8 Zeichen.'); setSaving(false); return; }
      const r = await apiFetch('/api/providers', { method: 'POST', body: JSON.stringify(form) });
      const d = await r?.json();
      if (d?.status) { showFb('success', 'Benutzer erstellt.'); setIsNew(false); load(); }
      else showFb('error', d?.detail || 'Fehler.');
    } else {
      const u = filtered[selectedIdx];
      const body = { ...form };
      if (!body.password) delete body.password;
      const r = await apiFetch(`/api/providers/${u.id}`, { method: 'PUT', body: JSON.stringify(body) });
      const d = await r?.json();
      if (d?.status) { showFb('success', 'Gespeichert.'); load(); }
      else showFb('error', d?.detail || 'Fehler.');
    }
    setSaving(false);
  };

  const toggleStatus = async (u) => {
    const newStatus = u.user_status === 'allow' ? 'block' : 'allow';
    await apiFetch(`/api/providers/${u.id}/status`, { method: 'PUT', body: JSON.stringify({ user_status: newStatus }) });
    load();
  };

  const archiveUser = async (id) => {
    if (!window.confirm('Benutzer wirklich archivieren?')) return;
    await apiFetch(`/api/providers/${id}/archive`, { method: 'POST' });
    setSelectedIdx(null);
    showFb('success', 'Benutzer archiviert.');
    load();
  };

  const restoreUser = async (id) => {
    await apiFetch(`/api/providers/${id}/restore`, { method: 'POST' });
    showFb('success', 'Benutzer wiederhergestellt.');
    load();
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Benutzer ENDGÜLTIG löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
    await apiFetch(`/api/providers/${id}`, { method: 'DELETE' });
    setSelectedIdx(null);
    showFb('success', 'Benutzer endgültig gelöscht.');
    load();
  };

  const impersonateUser = async (u) => {
    if (!window.confirm(`Als "${u.verein || u.email}" anmelden?`)) return;
    const r = await apiFetch(`/api/auth/impersonate/${u.id}`, { method: 'POST' });
    const d = await r?.json();
    if (d?.token) {
      localStorage.setItem('bgpr_original_token', localStorage.getItem('bgpr_token'));
      localStorage.setItem('bgpr_original_user_type', localStorage.getItem('bgpr_user_type'));
      localStorage.setItem('bgpr_original_email', localStorage.getItem('bgpr_email'));
      localStorage.setItem('bgpr_impersonating', d.original_admin_email);
      localStorage.setItem('bgpr_token', d.token);
      localStorage.setItem('bgpr_user_type', d.user_type);
      localStorage.setItem('bgpr_email', d.email || '');
      localStorage.setItem('bgpr_user_id', d.user_id);
      window.location.href = '/app';
    } else {
      showFb('error', d?.detail || 'Impersonation fehlgeschlagen');
    }
  };

  const activeCount = users.filter(u => u.user_status === 'allow').length;
  const blockedCount = users.filter(u => u.user_status === 'block').length;
  const showDetail = selectedIdx !== null || isNew;
  const currentUser = selectedIdx !== null ? filtered[selectedIdx] : null;

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Detail/Edit View
  if (showDetail) {
    return (
      <div data-testid="users-page">
        <div className="detail-nav-bar">
          <button className="btn btn-secondary btn-sm" onClick={goBack} data-testid="user-back-btn">
            <ArrowLeft size={14} /> Zurück zur Liste
          </button>
          {selectedIdx !== null && (
            <div className="detail-nav-pager">
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)} disabled={selectedIdx === 0} data-testid="user-prev-btn">
                <ChevronLeft size={14} /> Vorheriger
              </button>
              <span className="detail-nav-count">{selectedIdx + 1} / {filtered.length}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(1)} disabled={selectedIdx === filtered.length - 1} data-testid="user-next-btn">
                Nächster <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>{isNew ? 'Neuer Benutzer' : (form.verein || 'Benutzer bearbeiten')}</h2>
            {currentUser && <p>Club-ID: {currentUser.club_id} &middot; {currentUser.user_status === 'allow' ? 'Aktiv' : 'Gesperrt'}</p>}
          </div>
          {currentUser && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className={`btn btn-sm ${currentUser.user_status === 'allow' ? 'btn-secondary' : 'btn-primary'}`}
                onClick={() => toggleStatus(currentUser)} data-testid="user-toggle-status">
                {currentUser.user_status === 'allow' ? 'Sperren' : 'Aktivieren'}
              </button>
              {!showArchived ? (
                <button className="btn btn-secondary btn-sm" onClick={() => archiveUser(currentUser.id)} data-testid="user-archive-detail">
                  <Archive size={14} /> Archivieren
                </button>
              ) : (
                <>
                  <button className="btn btn-primary btn-sm" onClick={() => restoreUser(currentUser.id)} data-testid="user-restore-detail">
                    <ArchiveRestore size={14} /> Wiederherstellen
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteUser(currentUser.id)} data-testid="user-delete-detail">
                    <Trash2 size={14} /> Endgültig löschen
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {feedback && <div className={`design-feedback ${feedback.type}`} data-testid="users-feedback">{feedback.text}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
            {/* Left: Stammdaten */}
            <div className="card">
              <div className="card-body">
                <h4 className="detail-section-title">Stammdaten</h4>
                <div className="form-group"><label>Name der Einrichtung *</label><input className="form-input" value={form.verein} onChange={e => set('verein', e.target.value)} required data-testid="user-form-verein" /></div>
                <div className="form-group"><label>Club-ID (Benutzername) *</label><input className="form-input" value={form.club_id} onChange={e => set('club_id', e.target.value)} required data-testid="user-form-club-id" /></div>
                <div className="form-group"><label>E-Mail *</label><input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required data-testid="user-form-email" /></div>
                <div className="form-group"><label>IK-Nummer</label><input className="form-input" value={form.ik_number} onChange={e => set('ik_number', e.target.value)} data-testid="user-form-ik" /></div>
                <div className="form-group"><label>Ansprechpartner</label><input className="form-input" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} data-testid="user-form-contact" /></div>
                <div className="form-group"><label>Passwort {isNew ? '*' : '(leer = unverändert)'}</label><input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={isNew ? 'Min. 8 Zeichen' : '(unverändert)'} {...(isNew && { required: true, minLength: 8 })} data-testid="user-form-password" /></div>
              </div>
            </div>
            {/* Right: Adresse & Kontakt */}
            <div className="card">
              <div className="card-body">
                <h4 className="detail-section-title">Adresse & Kontakt</h4>
                <div className="form-group"><label>Straße</label><input className="form-input" value={form.street} onChange={e => set('street', e.target.value)} /></div>
                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: 120 }}><label>PLZ</label><input className="form-input" value={form.postal_code} onChange={e => set('postal_code', e.target.value)} /></div>
                  <div className="form-group"><label>Ort</label><input className="form-input" value={form.city_location} onChange={e => set('city_location', e.target.value)} /></div>
                </div>
                <div className="form-group"><label>Telefon</label><input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                <div className="form-group"><label>Website</label><input className="form-input" value={form.website} onChange={e => set('website', e.target.value)} /></div>
              </div>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving} data-testid="user-form-submit" style={{ marginTop: '1rem' }}>
            <Save size={14} /> {saving ? 'Speichern...' : (isNew ? 'Erstellen' : 'Aktualisieren')}
          </button>
        </form>
      </div>
    );
  }

  // List View
  return (
    <div data-testid="users-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2>{showArchived ? 'Archivierte Benutzer' : 'Benutzer'}</h2>
          <p>{users.length} {showArchived ? 'archivierte' : ''} Herzgruppenanbieter {!showArchived && <>&middot; {activeCount} aktiv &middot; {blockedCount} gesperrt</>}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${showArchived ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => { setShowArchived(!showArchived); setSelectedIdx(null); }} data-testid="toggle-archived-users">
            {showArchived ? <><Users size={14} /> Aktive anzeigen</> : <><Archive size={14} /> Archiv anzeigen</>}
          </button>
          {!showArchived && <button className="btn btn-primary" onClick={openNew} data-testid="add-user-btn"><Plus size={16} /> Neuer Benutzer</button>}
        </div>
      </div>

      {feedback && <div className={`design-feedback ${feedback.type}`} data-testid="users-feedback">{feedback.text}</div>}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} style={{ color: 'var(--text-secondary)' }} />
            <input className="form-input" placeholder="Name, Club-ID oder E-Mail suchen..." value={search} onChange={e => setSearch(e.target.value)} data-testid="users-search" style={{ border: 'none', padding: '6px 0', boxShadow: 'none' }} />
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>Laden...</p></div> : (
          <div className="table-wrap">
            <table data-testid="users-table">
              <thead><tr><th>Club-ID</th><th>Einrichtung</th><th>E-Mail</th><th>Letzter Login</th><th>Status</th><th>Aktionen</th></tr></thead>
              <tbody>
                {filtered.map((u, idx) => (
                  <tr key={u.id} ref={el => rowRefs.current[u.id] = el} data-testid={`user-row-${u.id}`}
                    onClick={() => selectUser(idx)} style={{ cursor: 'pointer' }}>
                    <td><span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{u.club_id || '-'}</span></td>
                    <td style={{ fontWeight: 500 }}>{u.verein || '-'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{u.email || '-'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {u.last_login ? new Date(u.last_login).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Nie'}
                    </td>
                    <td>
                      <span className={`badge ${u.user_status === 'allow' ? 'badge-success' : 'badge-danger'}`} data-testid={`user-status-${u.id}`}>
                        {u.user_status === 'allow' ? 'Aktiv' : 'Gesperrt'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="table-actions">
                        <button className="btn-icon edit" onClick={() => selectUser(idx)} title="Bearbeiten" data-testid={`user-edit-${u.id}`}><Pencil size={14} /></button>
                        {showArchived ? (
                          <button className="btn-icon cert" onClick={() => restoreUser(u.id)} title="Wiederherstellen" data-testid={`user-restore-${u.id}`}><ArchiveRestore size={14} /></button>
                        ) : (
                          <button className="btn-icon delete" onClick={() => archiveUser(u.id)} title="Archivieren" data-testid={`user-archive-${u.id}`}><Archive size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>{search ? 'Keine Ergebnisse.' : 'Keine Benutzer vorhanden.'}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
